import React, { memo, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _debounce from 'lodash/debounce';
import _get from 'lodash/get';
import { AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import useIsMobileDevice from '@lib/hooks/useIsMobileDevice';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import { ErrorPageStatusEnum } from '@lib/components/ErrorPage/enums';
import useWindowDimensions from '@lib/hooks/useWindowDimensions';
import useIsOnline from '@lib/hooks/useIsOnline';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import AssetCounterIndexModalForm from 'components/AssetCounterIndexModalForm/AssetCounterIndexModalForm';
import { FormRef } from '@lib/components/ReactHookForm/types';
import usePrevious from '@lib/hooks/usePrevious';
import Modal from '@lib/components/Modal/Modal';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import useModalManagement from './utils/useModalManagement';
import useActions from './utils/useActions';
import QrcodeScanner from './QrcodeScanner';
import styles from './QrcodeScanner.module.scss';

const { VITE_BLOCK_UI_BY_DEVICE } = import.meta.env;
const shouldBlockUiByDevice = VITE_BLOCK_UI_BY_DEVICE === 'true';

type Props = {
  closeScanner: () => void;
  getUserAccessByCompany: (
    component: AccessComponentEnum,
  ) => undefined | AccessLevelEnum;
  getUserAccessByComponent: (
    component: AccessComponentEnum,
  ) => undefined | AccessLevelEnum;
};

function QrcodeScannerContainer({
  closeScanner,
  getUserAccessByCompany,
  getUserAccessByComponent,
}: Props) {
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const isMobileDevice = useIsMobileDevice();
  const isOnline = useIsOnline();
  const [isLoading, setLoading] = useState(false);
  const isLoadingRef = useRef(false);
  const [scannerRenderDate, setScannerRenderDate] = useState(
    new Date().toISOString(),
  );
  const [errorStatus, setErrorStatus] = useState<
    ErrorPageStatusEnum | undefined
  >(undefined);
  const { id = '' } = useParams();
  const windowDimensions = useWindowDimensions();
  const prevWindowDimensions = usePrevious(windowDimensions);
  const useModalManagementResult = useModalManagement();
  const [isPageReady, setIsPageReady] = useState(false);

  const { width, height } = windowDimensions;
  const prevWidth = _get(prevWindowDimensions, 'width');
  const prevHeight = _get(prevWindowDimensions, 'height');

  const useActionsResult = useActions({
    id,
    isOnline,
    setErrorStatus,
    setLoading,
    useModalManagementResult,
    setScannerRenderDate,
    closeScanner,
    isLoadingRef,
  });
  const {
    assetCounterIndexMutationError,
    assetCounterIndexMutationLoading,
    counterQueryHookResult,
    loading,
    onCloseModal,
    onCounterIndexFormSubmit,
    qrCodeSuccessCallback,
  } = useActionsResult;

  const isLoadingState = !isPageReady || (!!id && loading);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    if (shouldBlockUiByDevice) {
      if (isMobileDevice) {
        setErrorStatus(undefined);
      } else {
        setErrorStatus(ErrorPageStatusEnum.MobileOnly);
      }
    }
    setIsPageReady(true);
  }, [isMobileDevice, width]);

  useEffect(() => {
    const handleWidthChange = _debounce(() => {
      const isWidthChanged = prevWidth && width && prevWidth !== width;
      const isHeightChanged = prevHeight && height && prevHeight !== height;
      const isWindowChanged = isWidthChanged || isHeightChanged;
      if (isWindowChanged && !loading) {
        setScannerRenderDate(new Date().toISOString());
      }
    }, 300);

    handleWidthChange();

    return () => {
      handleWidthChange.cancel();
    };
  }, [width, prevWidth, loading, prevHeight, height]);

  return (
    <>
      <Modal
        isOpen
        onClose={closeScanner}
        modalContentClassName={styles.modalContentClassName}
        modalBodyClassName={styles.modalBodyClassName}
        caption={t('scanner-description')}
      >
        <>
          {isLoadingState && (
            <div className={styles.loadingWrap}>
              <CircleLoader size={48} strokeWidth={4} />
            </div>
          )}
          {errorStatus && (
            <ErrorPage
              status={errorStatus}
              btnOnClickHandler={() => setErrorStatus(undefined)}
            />
          )}
          {!isLoadingState && !errorStatus && (
            <QrcodeScanner
              qrCodeSuccessCallback={qrCodeSuccessCallback}
              key={scannerRenderDate}
              getUserAccessByCompany={getUserAccessByCompany}
              getUserAccessByComponent={getUserAccessByComponent}
              loading={isLoading}
            />
          )}
        </>
      </Modal>
      <AssetCounterIndexModalForm
        assetCounter={counterQueryHookResult.data}
        assetCounterError={counterQueryHookResult.error}
        formRef={formRef}
        graphQLErrors={getGraphQLErrors(assetCounterIndexMutationError)}
        isOpen={useModalManagementResult.isCounterIndexFormModalOpened}
        loading={
          assetCounterIndexMutationLoading || counterQueryHookResult.loading
        }
        onCancel={onCloseModal}
        onClose={onCloseModal}
        onSubmit={onCounterIndexFormSubmit}
      />
    </>
  );
}

export default memo(QrcodeScannerContainer);
